import { useState } from 'react';

import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { debounce } from 'lodash';
import { v4 as uuid } from 'uuid';

import { UseGetFlag } from '../../app/launchDarkly';
import { IngredientsQueySearchData } from '../../app/recipeCreation/createRecipe.types';
import { getQueryIngredientsList } from '../../app/search/search.thunk';
import { useAppDispatch, useAppSelector } from '../../app/store/hooks';
import useLogRecipeEdit from '../../hooks/useLogRecipeEdit';
import { CloseIcon } from '../../iconRef/recipePageIcons';
import { EventLogsEnum } from '../../utils/events';
import IngredientCard from './ingredientCard';
import { CreateIngredientType, INGREDIENTS_TITLE, Mode } from './ingredients.types';
import styles from "./ingredientsPopup.module.css";
import LabelConversion from './ingredientUnitsConversion';

const filter = createFilterOptions<CreateIngredientType>();

const algoliaIngredientToData = (alIngredient: any) => {

    const { id, metadata: { defaultWeightUnit, name, secondaryUnit, url } } = alIngredient;

    return {
        id,
        displayName: name,
        defaultWeightUnit,
        secondaryUnit,
        image: url,
        isNewIngredient: false,
    }
};

const createIngredientData = () => ({
    id: uuid(),
    isNewIngredient: true,
    displayName: '',
    defaultWeightUnit: null,
    secondaryUnit: null,
    image: null,
})

const updateIngredientName = (newIngredientData: any, ingredientData: any) => {
    const ingData = (ingredientData?.isNewIngredient && ingredientData?.id) ? ingredientData : createIngredientData();
    return ({
        ...ingData,
        displayName: newIngredientData?.inputValue ?? '',
    });
}

const ingredientsConversion = (ingredient: any) => {
    const defaultWeightUnit = ingredient?.defaultWeightUnit
    const secondaryUnit = ingredient?.secondaryUnit
    if (defaultWeightUnit && secondaryUnit) {
        return [defaultWeightUnit, secondaryUnit]
    }
    if (defaultWeightUnit) {
        return [defaultWeightUnit]
    }
    if (secondaryUnit) {
        return [secondaryUnit]
    }
    if (!defaultWeightUnit && !secondaryUnit) {
        return ["piece(s)", "g"]
    }
    return ingredient;
}

const IngredientsCard = ({ option }: { option: any }) => {
    if (option?.metadata?.name) {
        return (
            <IngredientCard
                ingredientImage={option?.metadata?.url}
                ingredientName={option?.metadata?.name}
            />
        )
    }
    return null
}

function unitsConversion(ingredient: any) {
    const ingredientUnitsList = ingredientsConversion(ingredient);
    const convertSpoons = ingredientUnitsList.map((unit: any) => LabelConversion(unit))
    return convertSpoons
}

const IngredientsPopup = ({ open, onClose, ingredientModalMode, ingredientData, setIngredients, ingredients, ingredientsIndexToEdit }: { open: boolean, onClose: () => void, ingredientModalMode: string | null, ingredientData: any, setIngredients: any, ingredients: any, ingredientsIndexToEdit: number }) => {
    const [quantity, setQuantity] = useState<string>(ingredientModalMode === Mode.EDIT ? ingredientData?.amount?.qty : '');
    const [units, setUnits] = useState<string>(ingredientModalMode === Mode.EDIT ? ingredientData?.amount?.unit : '');
    const [ingredientDataEdit, setIngredientDataEdit] = useState<any>(ingredientData ?? {});
    const [showError, setShowError] = useState(false);
    const dispatch = useAppDispatch();
    const { enableAlgoliaSearch } = UseGetFlag();
    const { logRecipeEdit } = useLogRecipeEdit();

    const debouncedQuerySearchService = debounce((queryString: string) => dispatch(getQueryIngredientsList({ enableAlgoliaSearch, queryString })), 500)
    const searchFilteredIngredients = useAppSelector((state) => state.search.searchFilteredIngredients);

    const handleDoneClick = () => {
        if (!ingredientDataEdit?.displayName || !quantity || !units) {
            setShowError(true);
        } else {
            const newIngredient = {
                ...ingredientDataEdit,
                amount: { qty: Number(quantity), unit: units }
            }

            if (ingredientModalMode === Mode.EDIT) {
                const ing = ingredients.toSpliced(ingredientsIndexToEdit, 1, newIngredient);
                setIngredients(ing);

            } else if (ingredientModalMode === Mode.ADD) {
                const ing = [...ingredients];
                ing.push(newIngredient)
                setIngredients(ing);
            }
            logRecipeEdit(EventLogsEnum.APP_ING_CHANGES, {
                eventData: { ing_name: ingredientDataEdit?.displayName, qty: quantity, unit: units }
            })
            setShowError(false);
            onClose();
        }
    };

    const unitsWithConversion = unitsConversion(ingredientDataEdit);

    return (
        <Dialog
            onClose={onClose}
            open={open}
            PaperProps={{ className: styles.dialog_paper }}
        >
            <Grid>
                <Grid className={styles.close_btn}>
                    <IconButton onClick={onClose}><CloseIcon /></IconButton>
                </Grid>
                <Grid className={styles.container}>
                    <Grid className={styles.title} item>
                        <Typography variant='caption'>{ingredientModalMode === Mode.EDIT ? INGREDIENTS_TITLE.EDIT_INGREDIENTS : INGREDIENTS_TITLE.ADD_INGREDIENTS}</Typography>
                    </Grid>
                    <Grid>
                        <Autocomplete
                            classes={{ paper: styles.paper }}
                            disableClearable
                            filterOptions={(options, params) => {
                                const { inputValue } = params;
                                const inputValueLower = inputValue.toLowerCase();

                                const isIngredientExist = (ingredient: IngredientsQueySearchData) => {
                                    const ingredientName = ingredient?.metadata.name?.toLowerCase();
                                    const alternateNames = (ingredient?.metadata?.alternateNames || []).map(name => name.toLowerCase());
                                    return ingredientName === inputValueLower || alternateNames.includes(inputValueLower);
                                };
                                const doesNotExist = options.length === 0 || !options.some(isIngredientExist);
                                if (inputValue && doesNotExist) {
                                    options.unshift({
                                        inputValue,
                                        name: `+ Create "${inputValue}"`,
                                        create: true
                                    });
                                }
                                return options;
                            }}
                            freeSolo
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') {
                                    return option
                                }
                                return option.inputValue || option?.metadata?.name
                            }}
                            onChange={(event, newValue) => {
                                if (newValue?.id) {
                                    setIngredientDataEdit(algoliaIngredientToData(newValue));
                                } else {
                                    setIngredientDataEdit(updateIngredientName(newValue, ingredientDataEdit));
                                }
                            }}
                            options={searchFilteredIngredients}
                            renderInput={(params) => (
                                <TextField {...params}
                                    FormHelperTextProps={{ className: styles.error_text }}
                                    fullWidth
                                    helperText={!ingredientDataEdit?.displayName && showError ? "*Please enter the details." : ''}
                                    onChange={(e) => debouncedQuerySearchService(e.target.value)}
                                    placeholder='Search Ingredient'
                                    size="small"
                                    type="text"
                                />
                            )}
                            renderOption={(props, option) => {
                                if (option.create) {
                                    return (
                                        <div key={option.id}>
                                            {searchFilteredIngredients?.length === 0 &&
                                                <>
                                                    <Typography className={styles.no_results}> No results found</Typography>
                                                    <Divider className={styles.divider} variant='middle' />
                                                </>
                                            }
                                            <li {...props} className={styles.create_option}>
                                                <Typography variant='caption3Light'>{option.name}</Typography>
                                            </li>
                                        </div>
                                    );
                                }
                                return (
                                    <div key={option.id}>
                                        <li {...props}>
                                            <IngredientsCard option={option} />
                                        </li>
                                    </div>
                                );
                            }}
                            value={ingredientDataEdit?.displayName}
                        />
                        <TextField
                            className={styles.text_field}
                            FormHelperTextProps={{ className: styles.error_text }}
                            fullWidth
                            helperText={!quantity && showError ? "*Please enter the details." : ''}
                            onChange={e => setQuantity(e.target.value)}
                            placeholder='Add Quantity'
                            size="small"
                            type='number'
                            value={quantity}
                        />

                        <FormControl fullWidth>
                            <InputLabel
                                className={styles.unit_label} >Unit</InputLabel>
                            <Select
                                label="Unit"
                                onChange={e => setUnits(e.target.value)}
                                size="small"
                                value={units}
                            >
                                {unitsWithConversion?.map((unit: any) => (
                                    <MenuItem value={unit.value}>{unit.label}</MenuItem>
                                ))}
                            </Select>
                            <FormHelperText className={styles.units_error_message}>
                                {!units && showError ? "*Please enter the details." : ''}
                            </FormHelperText>
                        </FormControl>

                    </Grid>
                    <Button className={styles.done_btn} onClick={handleDoneClick}>
                        <Typography variant='body5'>Done</Typography>
                    </Button>
                </Grid>
            </Grid>
        </Dialog >
    )
}
export default IngredientsPopup;

